// ── BCHNostr — Create a Business Page ─────────────────────────────
//
// A Business Page is a SEPARATE Nostr identity the owner controls, not a
// tab bolted onto their personal profile. Creating one:
//
//   1. mints a fresh keypair (held by the app; see lib/identities.ts),
//   2. publishes — signed by the BUSINESS key — its kind 0 profile, its
//      kind 30499 listing, and a kind 30078 operator pointer back to the
//      owner,
//   3. publishes — signed by the OWNER (window.nostr) — the owner→businesses
//      list, so the link is mutually verifiable,
//   4. shows the business nsec ONCE for backup, because the app holds the
//      only copy of that key.
//
// The Page then behaves like any other account: its own profile, posts,
// discussion, and — because the tip wallet is keyed per-pubkey — its own
// BCH tip wallet. The owner acts as it via the identity switcher.

import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Store, Loader2, Check, Sparkles, AlertTriangle, Copy,
  Image as ImageIcon, Trash2, ShieldAlert, ArrowRightLeft,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { uploadImage } from '../lib/imageUpload';
import {
  BUSINESS_KIND, BUSINESS_CATEGORIES, buildBusinessEvent,
  PROFESSIONAL_TYPES, type ProfessionalType,
} from '../lib/business';
import {
  createBusinessIdentity, businessesOwnedBy, publishAsBusiness,
  buildOwnerListEvent, buildOperatorEvent,
} from '../lib/identities';

interface CreateBusinessModalProps {
  onClose: () => void;
  /** Called after the Page is created + the owner confirms the backup. */
  onCreated?: (businessPubkey: string) => void;
  /**
   * When true, the backup screen offers a single action that switches straight
   * into the new Business Page (no "stay personal" option). Used by the
   * post-signup flow, where the person signed up specifically to become a
   * business and should land as it.
   */
  autoSwitch?: boolean;
}

type Phase = 'form' | 'publishing' | 'backup';

export default function CreateBusinessModal({ onClose, onCreated, autoSwitch }: CreateBusinessModalProps) {
  const { user, setShowLoginDialog, switchToBusiness } = useAppContext();
  const { publish } = useNostrPublish();

  const [name, setName] = useState('');
  const [proType, setProType] = useState<ProfessionalType>('business');
  const [category, setCategory] = useState('other');
  const [tagline, setTagline] = useState('');
  const [bch, setBch] = useState('');
  const [picture, setPicture] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [location, setLocation] = useState('');

  const [phase, setPhase] = useState<Phase>('form');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Backup screen state.
  const [createdPubkey, setCreatedPubkey] = useState('');
  const [createdNsec, setCreatedNsec] = useState('');
  const [copied, setCopied] = useState(false);
  const [backupConfirmed, setBackupConfirmed] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      setPicture(await uploadImage(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    if (user.isBusiness) {
      setError('Switch to your personal account before creating a Business Page.');
      return;
    }
    const trimmedName = name.trim();
    if (!trimmedName) { setError('Give your business a name.'); return; }

    setError('');
    setPhase('publishing');
    const now = Math.floor(Date.now() / 1000);

    try {
      // 1. Mint the business keypair (persisted + registered as owned).
      const created = createBusinessIdentity(user.pubkey, { name: trimmedName, picture });
      // Tell the IdentitySwitcher a new Page exists so it refreshes without a
      // reload (the registry is written synchronously above).
      try { window.dispatchEvent(new CustomEvent('bchnostr:business-created')); } catch { /* ignore */ }
      const bizAddr = bch.trim().replace(/^bitcoincash:/i, '');

      // 2a. Business kind 0 — its own profile identity.
      const profileContent = JSON.stringify({
        name: trimmedName,
        about: tagline.trim() || `A Bitcoin Cash ${PROFESSIONAL_TYPES.find(t => t.id === proType)?.label.toLowerCase() || 'business'} on BCHNostr.`,
        picture: picture || '',
      });
      await publishAsBusiness(created.secretKey, {
        kind: 0, content: profileContent, tags: [], created_at: now,
      });

      // 2b. Business kind 30499 — the listing.
      const { tags: bizTags, content: bizContent } = buildBusinessEvent({
        name: trimmedName,
        category,
        proType,
        location: onlineOnly ? '' : location.trim(),
        bch: bizAddr || undefined,
        onlineOnly,
        content: { tagline: tagline.trim() || undefined },
      });
      await publishAsBusiness(created.secretKey, {
        kind: BUSINESS_KIND, content: bizContent, tags: bizTags, created_at: now,
      });

      // 2c. Business → owner pointer.
      const op = buildOperatorEvent(user.pubkey);
      await publishAsBusiness(created.secretKey, {
        kind: op.kind, content: op.content, tags: op.tags, created_at: now,
      });

      // 3. Owner → businesses list (signed by the owner via window.nostr).
      const owned = businessesOwnedBy(user.pubkey).map(b => b.pubkey);
      const list = buildOwnerListEvent(owned);
      await publish({ kind: list.kind, content: list.content, tags: list.tags, created_at: now });

      setCreatedPubkey(created.pubkey);
      setCreatedNsec(created.nsec);
      setPhase('backup');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the business Page.');
      setPhase('form');
    }
  };

  const copyNsec = async () => {
    try {
      await navigator.clipboard.writeText(createdNsec);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — select the key and copy it manually.');
    }
  };

  const finish = (thenSwitch: boolean) => {
    onCreated?.(createdPubkey);
    if (thenSwitch) {
      // Reloads into the new Page.
      switchToBusiness(createdPubkey);
    } else {
      onClose();
    }
  };

  // ── Logged out ──
  if (!user) {
    return createPortal(
      <Backdrop onClose={onClose}>
        <Panel>
          <Header title="Create a Business Page" onClose={onClose} />
          <div className="px-5 pb-5 text-center space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Log in with your personal Nostr account first. Your Business Page is a
              separate identity that your account owns and operates.
            </p>
            <button
              onClick={() => { setShowLoginDialog(true); onClose(); }}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold"
            >
              Log in
            </button>
          </div>
        </Panel>
      </Backdrop>,
      document.body,
    );
  }

  // ── Backup (one-time nsec reveal) ──
  if (phase === 'backup') {
    return createPortal(
      <Backdrop onClose={backupConfirmed ? onClose : () => {}}>
        <Panel>
          <Header title="Back up your Business Page" onClose={backupConfirmed ? onClose : undefined} />
          <div className="px-5 pb-5 space-y-4">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 text-emerald-500" />
            </div>
            <p className="text-sm font-bold text-center text-emerald-500">
              Your Business Page is live
            </p>

            <div className="rounded-xl border-2 border-amber-500/40 bg-amber-500/[0.06] p-3 space-y-2">
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                <ShieldAlert className="w-4 h-4 shrink-0" />
                <h4 className="text-xs font-bold uppercase tracking-wide">Save this key now</h4>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                This is the <strong>only</strong> copy of your Business Page's private key. The app
                keeps it in this browser, but if you clear your data or switch devices without it,
                the Page — and any BCH it holds — is gone for good. Store it somewhere safe.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 break-all text-[10px] font-mono bg-background rounded-lg px-2.5 py-2 border border-border">
                  {createdNsec}
                </code>
                <button
                  onClick={copyNsec}
                  className="shrink-0 px-2.5 py-2 rounded-lg bg-muted text-[11px] font-medium hover:bg-muted/70 flex items-center gap-1"
                >
                  {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
            </div>

            <label className="flex items-start gap-2 text-[11px] font-medium text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={backupConfirmed}
                onChange={e => setBackupConfirmed(e.target.checked)}
                className="accent-primary mt-0.5"
              />
              I've saved this key somewhere safe.
            </label>

            {autoSwitch ? (
              <button
                onClick={() => finish(true)}
                disabled={!backupConfirmed}
                className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-1.5 hover:bg-primary/90 disabled:opacity-50"
              >
                <ArrowRightLeft className="w-4 h-4" /> Continue to my Business Page
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => finish(true)}
                  disabled={!backupConfirmed}
                  className="flex-1 py-2.5 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-1.5 hover:bg-primary/90 disabled:opacity-50"
                >
                  <ArrowRightLeft className="w-4 h-4" /> Switch to Page
                </button>
                <button
                  onClick={() => finish(false)}
                  disabled={!backupConfirmed}
                  className="px-4 py-2.5 rounded-xl bg-muted text-sm font-medium disabled:opacity-50"
                >
                  Done
                </button>
              </div>
            )}
            {error && <p className="text-xs text-red-500 text-center">{error}</p>}
          </div>
        </Panel>
      </Backdrop>,
      document.body,
    );
  }

  const publishing = phase === 'publishing';

  // ── Form ──
  return createPortal(
    <Backdrop onClose={publishing ? () => {} : onClose}>
      <Panel>
        <Header title="Create a Business Page" onClose={publishing ? undefined : onClose} />
        <div className="px-5 pb-5 space-y-3.5 max-h-[80vh] overflow-y-auto">
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            A Business Page is its <strong>own</strong> account — separate from your personal
            profile, with its own posts and its own BCH tip wallet. You own it and can switch
            into it any time.
          </p>

          {/* Avatar + name */}
          <div className="flex items-start gap-3">
            {picture ? (
              <img src={picture} alt="" className="w-16 h-16 rounded-2xl object-cover border border-border shrink-0" />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-muted border border-dashed border-border flex items-center justify-center shrink-0">
                <Store className="w-6 h-6 text-muted-foreground/50" />
              </div>
            )}
            <div className="flex-1 min-w-0 space-y-1.5">
              <label className="text-[11px] font-medium text-muted-foreground">
                Business Name <span className="text-primary">*</span>
              </label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Bob's Bitcoin Cash Café"
                maxLength={100}
                autoFocus
                className="w-full bg-background rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="px-2.5 py-1 rounded-lg bg-muted text-[11px] font-medium hover:bg-muted/70 disabled:opacity-50 flex items-center gap-1"
                >
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                  {uploading ? 'Uploading' : 'Logo'}
                </button>
                {picture && (
                  <button
                    type="button"
                    onClick={() => setPicture('')}
                    className="p-1 rounded text-muted-foreground hover:text-red-500"
                    title="Remove logo"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />

          {/* Professional type */}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground">Type</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-1.5">
              {PROFESSIONAL_TYPES.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setProType(t.id)}
                  title={t.hint}
                  className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                    proType === t.id
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/40'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tagline */}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground">Tagline</label>
            <input
              value={tagline}
              onChange={e => setTagline(e.target.value)}
              placeholder="Best flat white in town — pay in BCH"
              maxLength={120}
              className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Category */}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground">Category</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {BUSINESS_CATEGORIES.map(c => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 text-[11px] font-medium text-foreground">
            <input
              type="checkbox"
              checked={onlineOnly}
              onChange={e => setOnlineOnly(e.target.checked)}
              className="accent-primary"
            />
            Online only (no physical location)
          </label>

          {!onlineOnly && (
            <div>
              <label className="text-[11px] font-medium text-muted-foreground">Location</label>
              <input
                value={location}
                onChange={e => setLocation(e.target.value)}
                placeholder="123 Main St, Springfield"
                maxLength={140}
                className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          )}

          {/* BCH address */}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground">BCH Address</label>
            <input
              value={bch}
              onChange={e => setBch(e.target.value)}
              placeholder="bitcoincash:q… or q… (optional)"
              maxLength={120}
              className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 font-mono focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Where customers' tips go. You can also use the Page's built-in tip wallet later.
            </p>
          </div>

          <button
            onClick={handleCreate}
            disabled={publishing || !name.trim()}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50"
          >
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {publishing ? 'Creating Page…' : 'Create Business Page'}
          </button>

          {error && (
            <p className="flex items-start gap-1.5 text-xs text-red-500">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {error}
            </p>
          )}
        </div>
      </Panel>
    </Backdrop>,
    document.body,
  );
}

// ── Chrome ────────────────────────────────────────────────────────

function Backdrop({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      {children}
    </div>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-popover border border-border shadow-2xl overflow-hidden"
      onClick={e => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

function Header({ title, onClose }: { title: string; onClose?: () => void }) {
  return (
    <div className="flex items-center justify-between px-5 pt-5 pb-3">
      <h2 className="font-bold text-base flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-emerald-600 flex items-center justify-center">
          <Store className="w-4 h-4 text-white" />
        </div>
        {title}
      </h2>
      {onClose && (
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
